import React from 'react';
import ColorField from './ColorField';
import { FONT_OPTIONS, SCALE_STYLE_OPTIONS, getContrastRatio, WCAG_AA_MIN_CONTRAST } from './surveyCustomize';

const COLOR_FIELDS = [
  { key: 'primary', label: 'Primary' },
  { key: 'success', label: 'Success' },
  { key: 'info', label: 'Info' },
  { key: 'danger', label: 'Danger' },
  { key: 'text', label: 'Text' },
  { key: 'page', label: 'Page' },
];

function CustomizePanel({ isOpen, onClose, config, onChange, matchHeight }) {
  const setColor = (key, value) => {
    onChange({ colors: { ...config.colors, [key]: value } });
  };

  const contrastRatio = getContrastRatio(config.colors.text, config.colors.page);
  const hasLowContrast = contrastRatio < WCAG_AA_MIN_CONTRAST;

  return (
    <aside
      className={`survey-customize-panel${isOpen ? ' open' : ''}`}
      aria-label="Customize theme"
      // inert (not aria-hidden) while closed — aria-hidden on a container
      // that still has focusable descendants (every input/select/button in
      // here) is an invalid ARIA pattern; inert correctly removes it from
      // both the tab order and the accessibility tree in one step. Passed
      // as a string, not a JS boolean — React 18 silently drops boolean
      // `true`/`false` for attributes outside its known-property list
      // instead of rendering `inert="true"`/omitting it as expected.
      inert={isOpen ? undefined : ''}
      style={matchHeight ? { height: matchHeight } : undefined}
    >
      <div className="survey-customize-inner">
        <div className="survey-customize-header">
          <h3>Customize</h3>
          <button type="button" className="survey-customize-close" onClick={onClose} aria-label="Close customize panel">
            close
          </button>
        </div>

        <details className="survey-customize-section" open>
          <summary><h4>Brand</h4></summary>
          <div className="survey-customize-section-body">
            <label className="survey-customize-field">
              <span>Name</span>
              <input
                type="text"
                value={config.wordmark}
                onChange={(e) => onChange({ wordmark: e.target.value })}
              />
            </label>
            <label className="survey-customize-field">
              <span>Tagline</span>
              <input
                type="text"
                value={config.tagline}
                onChange={(e) => onChange({ tagline: e.target.value })}
              />
            </label>
          </div>
        </details>

        <details className="survey-customize-section">
          <summary><h4>Colors</h4></summary>
          <div className="survey-customize-section-body">
            {COLOR_FIELDS.map(({ key, label }) => (
              <ColorField
                key={key}
                label={label}
                value={config.colors[key]}
                onChange={(value) => setColor(key, value)}
              />
            ))}
            {hasLowContrast && (
              <p className="survey-customize-warning">
                Text and Page don't meet WCAG AA contrast (ratio {contrastRatio.toFixed(1)}:1, needs 4.5:1).
              </p>
            )}
          </div>
        </details>

        <details className="survey-customize-section">
          <summary><h4>Typography</h4></summary>
          <div className="survey-customize-section-body">
            <label className="survey-customize-field">
              <span>Font</span>
              <select
                value={config.font}
                onChange={(e) => onChange({ font: e.target.value })}
              >
                {FONT_OPTIONS.map((font) => (
                  <option key={font.id} value={font.id}>{font.label}</option>
                ))}
              </select>
            </label>
          </div>
        </details>

        <details className="survey-customize-section">
          <summary><h4>Rating Scales</h4></summary>
          <div className="survey-customize-section-body">
            <label className="survey-customize-field">
              <span>Style</span>
              <select
                value={config.scaleStyle}
                onChange={(e) => onChange({ scaleStyle: e.target.value })}
              >
                {SCALE_STYLE_OPTIONS.map((style) => (
                  <option key={style.id} value={style.id}>{style.label}</option>
                ))}
              </select>
            </label>
          </div>
        </details>

        <details className="survey-customize-section">
          <summary><h4>Navigation</h4></summary>
          <div className="survey-customize-section-body">
            <label className="survey-customize-checkbox">
              <input
                type="checkbox"
                checked={config.autoAdvance}
                onChange={(e) => onChange({ autoAdvance: e.target.checked, hideNavButtons: e.target.checked ? config.hideNavButtons : false })}
              />
              <span>Auto-advance after a selection</span>
            </label>
            {config.autoAdvance && (
              <label className="survey-customize-checkbox survey-customize-checkbox--nested">
                <input
                  type="checkbox"
                  checked={config.hideNavButtons}
                  onChange={(e) => onChange({ hideNavButtons: e.target.checked })}
                />
                <span>Hide Back/Next buttons (except on the open-ended question)</span>
              </label>
            )}
          </div>
        </details>
      </div>
    </aside>
  );
}

export default CustomizePanel;
